<template>
	<div
		class="banner d-flex align-items-center justify-content-center"
		v-if="!isMobile && (showOverideHeader || (visible && challengeStarted && isStevenOrOrganic))"
		:style="{ backgroundColor: primaryColor }"
	>
		<div class="content d-flex align-items-center justify-content-center">
			<img v-if="prefixImage" :src="prefixImage" alt="logo" class="logo" />
			<span class="heading desktop" :style="{ color: secondaryColor }">{{ heading }}&nbsp;</span>
			<span class="heading mobile" :style="{ color: secondaryColor }">{{ mobileHeading }}&nbsp;</span>
			<CountdownTimer hideMobile v-if="endDate" :endDate="endDate" @timerEnded="closeOnEndDate" :showMonths="false" />
			<button class="button-class" v-if="linkText" @click="handleLinkClick" :style="{ color: primaryColor, backgroundColor: secondaryColor }">
				{{ linkText }}
			</button>
		</div>
		<img v-if="showCloseIcon" class="close-icon" src="@/assets/images/home/close-icon.svg" @click="closeBanner" />
	</div>
</template>
<script setup>
	import { onMounted, ref, defineProps, inject, defineEmits } from 'vue'
	import { useRoute } from 'vue-router'
	import CountdownTimer from '@/components/shared/CountdownTimer'
	import { computed } from 'vue'
	import { watch } from 'vue'
	import { useCookies } from 'vue3-cookies'
	const { cookies } = useCookies()
	const CLOSED = 'closed'
	const route = useRoute()

	const stanAnalytics = inject('stanAnalytics')
	const emit = defineEmits('toggle')
	const props = defineProps({
		bannerKey: {
			type: String,
		},
		heading: {
			type: String,
		},
		mobileHeading: {
			type: String,
		},
		endDate: {
			type: Date,
			default: undefined,
		},
		hideOnEndDate: {
			type: Boolean,
			default: false,
		},
		linkText: {
			type: String,
		},
		primaryColor: {
			type: String,
			default: '#30ffb4',
		},
		secondaryColor: {
			type: String,
			default: '#131F60',
		},
		showCloseIcon: {
			type: Boolean,
			default: true,
		},
		prefixImage: {
			type: String,
			default: '',
		},
	})
	const visible = ref(false)
	const isMobile = ref(false)

	onMounted(() => {
		isMobile.value = window.innerWidth <= 768
		window.addEventListener('resize', () => {
			isMobile.value = window.innerWidth <= 768
		})
		setVisible(props.bannerKey && !(sessionStorage.getItem(props.bannerKey) === CLOSED))
	})

	watch(isMobile, newVal => {
		if (visible.value) {
			const shouldRender = visible.value && challengeStarted.value && !newVal
			emit('toggle', shouldRender) // send `true` if not mobile, false if mobile
		}
	})

	const closeOnEndDate = () => {
		stanAnalytics('banner-time-expired', {
			meta: { username: 'guest' },
			props: { bannerKey: props.bannerKey },
		})

		if (props.hideOnEndDate) {
			stanAnalytics('banner-closed-expired', {
				meta: { username: 'guest' },
				props: { bannerKey: props.bannerKey },
			})

			sessionStorage.setItem(props.bannerKey, CLOSED)
			setVisible(false)
		}
	}

	const closeBanner = () => {
		sessionStorage.setItem(props.bannerKey, CLOSED)
		setVisible(false)

		stanAnalytics('banner-closed-by-user', {
			meta: { username: 'guest' },
			props: { bannerKey: props.bannerKey },
		})
	}

	const handleLinkClick = () => {
		const stevenbartlett = 'https://stevenbartlett.stan.store/?utm_source=website&utm_medium=banner&utm_campaign=stevenbartlett'
		const organic = 'https://stevenbartlett.stan.store/?utm_source=website&utm_medium=banner&utm_campaign=stevenbartlett&ref=sosb'

		if (isOrganic.value) {
			window.open(organic, '_self')
		} else if (isSteven.value) {
			window.open(stevenbartlett, '_self')
		}
	}

	const showOverideHeader = computed(() => route.query.header !== undefined)
	const challengeStarted = computed(() => {
		// 11:01am on the 24th of May 2025 UTC is the equivalent of 12:01am on the 25th of May 2025 BST (british summer time)
		return new Date().toISOString() >= '2025-05-24T23:01:00Z'
	})

	const isOrganic = computed(() => {
		const urlString = window.location.href
		const url = new URL(urlString)
		const urlRef = url.searchParams.get('ref')
		const cookieRef = cookies.get('ref')
		return !cookieRef && !urlRef
	})

	const isSteven = computed(() => {
		const urlString = window.location.href
		const url = new URL(urlString)
		const urlRef = url.searchParams.get('ref')
		const cookieRef = cookies.get('ref')

		if (cookieRef || urlRef) {
			return cookieRef === 'stevenbartlett' || urlRef === 'stevenbartlett'
		}

		return false
	})

	const isStevenOrOrganic = computed(() => {
		return isOrganic.value || isSteven.value
	})

	const setVisible = newStatus => {
		if (newStatus) {
			stanAnalytics('banner-visible', {
				meta: { username: 'guest' },
				props: { bannerKey: props.bannerKey },
			})
		}
		if (newStatus !== visible.value) {
			visible.value = newStatus
			if (showOverideHeader.value || challengeStarted.value) {
				emit('toggle', newStatus)
			}
		}
	}
</script>

<style lang="scss" scoped>
	.button-class {
		height: 40px;
		width: 125px;
		border-radius: 5.79px;
		font-family: 'Helvetica';
		font-weight: 700;
		line-height: 12.92px;
		letter-spacing: -0.13px;
		border: none;
		font-size: 12.92px;
	}
	.banner {
		min-height: 66px;
		padding-left: 25px;
		padding-right: 25px;
		font-size: 18px;

		.desktop {
			display: block;
		}
		.mobile {
			display: none;
		}

		.close-icon {
			cursor: pointer;
			margin-left: 25px;
		}

		@media screen and (max-width: 767px) {
			padding-left: 20px;
			padding-right: 20px;
			font-size: 14px;

			.desktop {
				display: none;
			}
			.mobile {
				display: block;
			}

			.close-icon {
				margin-left: 10px;
				height: 17px;
			}
		}

		.content {
			gap: 10px;
			margin: 0 auto;
			max-width: 100%;
		}

		.heading {
			color: var(--Text-Dark);
			font-family: 'Plus Jakarta Sans';
			font-style: normal;
			font-weight: 400;
			width: 300px;
			line-height: normal;
			letter-spacing: -0.36px;
			font-size: 13px;
		}

		a:link {
			color: var(--Text-Dark, #131f60);
			font-family: 'Plus Jakarta Sans';
			font-style: normal;
			font-weight: 400;
			line-height: normal;
			letter-spacing: -0.36px;
			text-decoration-line: underline;
			text-decoration-style: solid;
			text-decoration-skip-ink: auto;
			text-decoration-thickness: auto;
			text-underline-offset: auto;
			text-underline-position: from-font;
		}
	}
</style>
